import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setExpandedTabs, setSidebarOpen } from "./sidebarSlice";
import ReactTooltip from "react-tooltip";
import { ChevronDown } from "react-feather";
import { gtcTabsEnum, landedCostTabsEnum, RootState, TabItem } from "./sidebarEnum";
import { toast } from "react-toastify";
import { isInternalUser, isLandedCost, redirectToAccessiblePath } from "utils/const";

interface UserProfileData {
  email?: string;
}

const Sidebar = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isSideBarOpen = useSelector((state: RootState) => state.sidebar?.isOpen ?? false);
  const expandedTabs = useSelector((state: RootState) => state.sidebar?.expandedTabs ?? {});
  const userAccessKeys = useSelector((state: RootState) => state.userAccess?.accessKeys ?? {});
  const userProfileData = useSelector((state: { profiledata: { data: UserProfileData } }) => state.profiledata.data);
  const hasInternalAccess: boolean = isInternalUser(userProfileData?.email);
  const hasExpandedKeys: boolean = Object.keys(expandedTabs).length > 0;
  const homePath: string = isLandedCost ? "/landedcostcalculator" : redirectToAccessiblePath(userAccessKeys);

  useEffect(() => {
    if (!hasExpandedKeys) {
      const allTabs = [...landedCostTabsEnum.upperTabs, ...gtcTabsEnum.upperTabs, ...landedCostTabsEnum.bottomTabs, ...gtcTabsEnum.bottomTabs] as TabItem[];
      const defaultExpandedTabs: Record<string, boolean> = {};
      allTabs.forEach((tab) => {
        if (tab.id) {
          defaultExpandedTabs[tab.id] = !tab.defaultCollapsed;
        }
      });
      dispatch(setExpandedTabs(defaultExpandedTabs));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasExpandedKeys, isLandedCost]);

  const changeTab = (link: string): void => {
    try {
      if (link) {
        navigate(link);
      }
    } catch (error) {
      toast.error("Navigation error. Please try again.");
    }
  };

  const sidebarState = (): void => {
    dispatch(setSidebarOpen(!isSideBarOpen));
  };

  const toggleAccordion = (name: string): void => {
    if (!isSideBarOpen) return;
    dispatch(setExpandedTabs({ ...expandedTabs, [name]: !expandedTabs[name] }));
  };

  const hasAccess = (accessKey: string | undefined): boolean => {
    if (!accessKey) return true;
    return userAccessKeys[accessKey] === true;
  };

  const renderTabs = (tabGroup: TabItem[], defaultCollapsible: boolean = true): React.ReactNode => (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-2">
      {tabGroup.map((parent) => {
        const accessibleChildren =
          parent.subItem?.filter((child) => {
            if (child.link === "/hs-classifier" || child.link === "/eccn-classifier") {
              return hasInternalAccess;
            }
            return hasAccess(child.accessKey);
          }) || [];
        if (accessibleChildren.length === 0) return null;
        const collapsible = parent.collapsible ?? defaultCollapsible;
        const isOpen: boolean = collapsible ? !!expandedTabs[parent.id] : true;
        return (
          <div key={parent.name}>
            {collapsible && parent.name && isSideBarOpen && (
              <div
                onClick={() => toggleAccordion(parent.id)}
                className="tw-flex tw-gap-1 tw-items-center tw-rounded-lg tw-px-1.5 tw-py-1 tw-cursor-pointer tw-text-label-sm tw-font-medium tw-text-gray-500"
                data-for="dashboard-tooltip"
                data-tip={parent.name}>
                <span className={`tw-inline-flex tw-items-center tw-transition-transform tw-duration-300 tw-ease-in-out ${isOpen ? "tw-rotate-0" : "-tw-rotate-90"}`}>
                  <ChevronDown size={16} />
                </span>
                <p className="tw-mb-0">{parent.name}</p>
              </div>
            )}
            <div
              className={`tw-overflow-hidden tw-transition-[max-height] tw-duration-300 tw-ease-in-out ${isOpen || !isSideBarOpen ? "tw-max-h-[1000px]" : "tw-max-h-0"} ${
                isSideBarOpen && collapsible ? "tw-ml-[11px] tw-pl-[10px] tw-border-l tw-border-l-gray-300" : ""
              }`}>
              {accessibleChildren.map((child: TabItem) => {
                const isActive: boolean = window.location.href.includes(child.link);
                return (
                  <div
                    key={child.name}
                    className={`tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-cursor-pointer hover:tw-bg-[#d0d5dd80] ${
                      isActive ? "tw-bg-white tw-font-medium tw-shadow-[0_2px_4px_-2px_rgba(16,24,40,0.06),0_4px_8px_-2px_rgba(16,24,40,0.1)] hover:tw-bg-white" : ""
                    }`}>
                    <div
                      className={`tw-flex tw-gap-2 tw-w-full tw-items-center tw-text-label-md tw-text-gray-900 tw-p-1.5 tw-rounded-lg tw-min-w-0 ${!isSideBarOpen ? "tw-justify-center" : ""}`}
                      onClick={() => changeTab(child.link)}
                      data-for="dashboard-tooltip"
                      data-tip={child.name}>
                      {child.icon}
                      {isSideBarOpen && <p className="tw-mb-0 tw-flex-1 tw-min-w-0 tw-whitespace-nowrap">{child.name}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="tw-flex">
      <div className={`tw-text-gray-900 tw-text-label-md tw-bg-gray-100 tw-fixed tw-left-0 tw-z-[200] ${isSideBarOpen ? "tw-w-[260px]" : "tw-w-[60px]"}`}>
        <div className="tw-h-12 tw-border-b tw-border-b-[#e7e7e7] tw-flex tw-w-full tw-items-center tw-justify-between tw-ps-5 tw-pe-2">
          <a
            href={homePath}
            className="tw-flex tw-items-center tw-gap-1 tw-min-w-0 tw-text-gray-900 hover:tw-text-gray-900 tw-no-underline">
            <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1645085709/global/trademo_logo/justLogo_mr5y0j.svg" alt="trademo logo" height={20} width={20} />
            {isSideBarOpen && <span className="tw-text-label-lg tw-font-semibold tw-whitespace-nowrap">{isLandedCost ? "Trademo Landed Cost" : "Trademo GTC"}</span>}
          </a>
          {isSideBarOpen && (
            <button onClick={sidebarState} className="tw-border tw-bg-white tw-rounded-lg tw-p-0.5 tw-flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                <path d="M641.92-336.54v-286.92L498.08-480l143.84 143.46ZM212.31-140q-29.92 0-51.12-21.19Q140-182.39 140-212.31v-535.38q0-29.92 21.19-51.12Q182.39-820 212.31-820h535.38q29.92 0 51.12 21.19Q820-777.61 820-747.69v535.38q0 29.92-21.19 51.12Q777.61-140 747.69-140H212.31ZM320-200v-560H212.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v535.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85H320Zm60 0h367.69q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-535.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H380v560Zm-60 0H200h120Z" />
              </svg>
            </button>
          )}
        </div>
        <div className="tw-px-3 tw-py-2 tw-h-[calc(100vh-48px)] tw-overflow-y-auto tw-min-w-full">
          {!isSideBarOpen && (
            <button onClick={sidebarState} className="tw-border tw-bg-white tw-rounded-lg tw-ms-1 tw-p-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                <path d="M498.08-623.46v286.92L641.92-480 498.08-623.46ZM212.31-140q-29.92 0-51.12-21.19Q140-182.39 140-212.31v-535.38q0-29.92 21.19-51.12Q182.39-820 212.31-820h535.38q29.92 0 51.12 21.19Q820-777.61 820-747.69v535.38q0 29.92-21.19 51.12Q777.61-140 747.69-140H212.31ZM320-200v-560H212.31q-4.62 0-8.46 3.85-3.85 3.84-3.85 8.46v535.38q0 4.62 3.85 8.46 3.84 3.85 8.46 3.85H320Zm60 0h367.69q4.62 0 8.46-3.85 3.85-3.84 3.85-8.46v-535.38q0-4.62-3.85-8.46-3.84-3.85-8.46-3.85H380v560Zm-60 0H200h120Z" />
              </svg>
            </button>
          )}
          <div className={`tw-flex tw-flex-col tw-justify-between ${isSideBarOpen ? "tw-h-[calc(100vh-64px)]" : "tw-h-[calc(100vh-100px)] tw-mt-2"}`}>
            {renderTabs((isLandedCost ? landedCostTabsEnum.upperTabs : gtcTabsEnum.upperTabs) as TabItem[])}
            {renderTabs((isLandedCost ? landedCostTabsEnum.bottomTabs : gtcTabsEnum.bottomTabs) as TabItem[], false)}
          </div>
        </div>
      </div>
      <main
        className={`${window.location.pathname.includes("classify") ? "" : "tw-p-3"} tw-h-[calc(100vh-48px)] tw-transition-all tw-duration-300 tw-mt-[48px] ${
          isSideBarOpen ? "tw-ms-[260px]" : "tw-ms-[60px]"
        } tw-w-full tw-overflow-auto`}>
        {children}
      </main>
      {!isSideBarOpen && <ReactTooltip id="dashboard-tooltip" place="right" />}
    </div>
  );
};

export default Sidebar;
